import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";

type Recall = {
  id: string;
  due_date: string;
  days_overdue: number;
  requested_by_name: string;
  reason_text?: string;
};

/**
 * What this patient was meant to come back for.
 *
 * Nothing closes a recall on its own (ADR-018 point 4), so the doctor has to
 * see it at the moment they can act on it. That moment is opening the chart,
 * not a separate list they might visit later.
 *
 * The button says done, and the server records which visit satisfied it.
 */
export function RecallPrompt({
  patientId,
  isDoctor,
  onChanged,
}: {
  patientId: string;
  isDoctor: boolean;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<Recall[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: failed } = await api.GET("/portal/patients/{id}/recalls", {
      params: { path: { id: patientId }, header: clinicHeader() },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Recall[]);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const complete = async (row: Recall) => {
    setBusy(row.id);
    // No request body: completing names no reason, and the server works out
    // which visit satisfied it.
    const { error: failed } = await api.POST("/portal/recalls/{id}/complete", {
      params: { path: { id: row.id }, header: clinicHeader() },
    });
    setBusy(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
    onChanged();
  };

  if (error || rows.length === 0) return null;

  return (
    <div className="recall-prompt">
      {rows.map((row) => (
        <div key={row.id} className="recall-prompt-row">
          <span className="grow">
            <strong>Due back:</strong> {row.reason_text ?? "Recall set"}
            <span className="muted">
              {" · "}
              {row.days_overdue > 0 ? `${row.days_overdue} days overdue` : `due ${row.due_date}`} ·
              set by {row.requested_by_name}
            </span>
          </span>
          {isDoctor && (
            <button type="button" disabled={busy === row.id} onClick={() => complete(row)}>
              Done at this visit
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
